import React, { useState, useEffect, useCallback, useRef } from 'react';
import { applyTheme, resetTheme } from '../hooks/useTheme';

const DEFAULTS = {
  nav_from: '#1d2f6f', nav_to: '#273c75', nav_active: '#17a2b8',
  primary: '#1f4e79', primary_dark: '#273c75', accent: '#17a2b8',
  header_bg: '#eaf0ff',
  text: '#374151', text_secondary: '#64748b', text_muted: '#94a3b8',
  border: '#e2e8f0', border_light: '#f1f5f9', bg_light: '#f8fafc',
  success: '#16a34a', danger: '#dc2626', warning: '#d97706',
  tab_active_border: '#17a2b8', tab_active_text: '#273c75',
  font_family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
};

const GROUPS = [
  {
    label: 'Navigation Bar',
    fields: [
      { key: 'nav_from', label: 'Nav Gradient Start', type: 'color' },
      { key: 'nav_to', label: 'Nav Gradient End', type: 'color' },
      { key: 'nav_active', label: 'Active Button', type: 'color' },
    ],
  },
  {
    label: 'Brand Colors',
    fields: [
      { key: 'primary', label: 'Primary', type: 'color' },
      { key: 'primary_dark', label: 'Primary Dark', type: 'color' },
      { key: 'accent', label: 'Accent', type: 'color' },
    ],
  },
  {
    label: 'Tabs & Headers',
    fields: [
      { key: 'tab_active_text', label: 'Active Tab Text', type: 'color' },
      { key: 'tab_active_border', label: 'Active Tab Border', type: 'color' },
      { key: 'header_bg', label: 'Table Header Background', type: 'color' },
    ],
  },
  {
    label: 'Text Colors',
    fields: [
      { key: 'text', label: 'Primary Text', type: 'color' },
      { key: 'text_secondary', label: 'Secondary Text', type: 'color' },
      { key: 'text_muted', label: 'Muted Text', type: 'color' },
    ],
  },
  {
    label: 'Backgrounds & Borders',
    fields: [
      { key: 'bg_light', label: 'Light Background', type: 'color' },
      { key: 'border', label: 'Border', type: 'color' },
      { key: 'border_light', label: 'Border Light', type: 'color' },
    ],
  },
  {
    label: 'Status Colors',
    fields: [
      { key: 'success', label: 'Success / Green', type: 'color' },
      { key: 'danger', label: 'Danger / Red', type: 'color' },
      { key: 'warning', label: 'Warning / Amber', type: 'color' },
    ],
  },
  {
    label: 'Typography',
    fields: [
      { key: 'font_family', label: 'Font Family', type: 'text' },
    ],
  },
];

const PRESETS = [
  { name: 'Default Blue', config: {} },
  { name: 'Ocean Teal', config: { nav_from: '#0f4c5c', nav_to: '#1a6b7a', nav_active: '#00b4d8', primary: '#0f4c5c', primary_dark: '#0a3640', accent: '#00b4d8', tab_active_text: '#0f4c5c', tab_active_border: '#00b4d8' } },
  { name: 'Emerald Green', config: { nav_from: '#064e3b', nav_to: '#065f46', nav_active: '#10b981', primary: '#064e3b', primary_dark: '#064e3b', accent: '#10b981', tab_active_text: '#064e3b', tab_active_border: '#10b981' } },
  { name: 'Royal Purple', config: { nav_from: '#3b0764', nav_to: '#4c1d95', nav_active: '#8b5cf6', primary: '#4c1d95', primary_dark: '#3b0764', accent: '#8b5cf6', tab_active_text: '#4c1d95', tab_active_border: '#8b5cf6' } },
  { name: 'Warm Burgundy', config: { nav_from: '#7f1d1d', nav_to: '#991b1b', nav_active: '#f59e0b', primary: '#7f1d1d', primary_dark: '#7f1d1d', accent: '#f59e0b', tab_active_text: '#7f1d1d', tab_active_border: '#f59e0b' } },
  { name: 'Charcoal Dark', config: { nav_from: '#111827', nav_to: '#1f2937', nav_active: '#6366f1', primary: '#1f2937', primary_dark: '#111827', accent: '#6366f1', tab_active_text: '#1f2937', tab_active_border: '#6366f1', header_bg: '#f1f5f9', bg_light: '#f9fafb' } },
  { name: 'Sunset Gold', config: { nav_from: '#78350f', nav_to: '#92400e', nav_active: '#fbbf24', primary: '#92400e', primary_dark: '#78350f', accent: '#f59e0b', header_bg: '#fef3c7', bg_light: '#fffbeb', tab_active_text: '#78350f', tab_active_border: '#f59e0b', border: '#e5e7eb', success: '#059669', danger: '#dc2626', warning: '#d97706' } },
  { name: 'Midnight Navy', config: { nav_from: '#0f172a', nav_to: '#1e293b', nav_active: '#38bdf8', primary: '#1e3a5f', primary_dark: '#0f172a', accent: '#38bdf8', header_bg: '#e0f2fe', bg_light: '#f0f9ff', tab_active_text: '#0f172a', tab_active_border: '#38bdf8', text: '#1e293b', text_secondary: '#475569', border: '#cbd5e1' } },
  { name: 'Rose Pink', config: { nav_from: '#881337', nav_to: '#9f1239', nav_active: '#fb7185', primary: '#9f1239', primary_dark: '#881337', accent: '#fb7185', header_bg: '#ffe4e6', bg_light: '#fff1f2', tab_active_text: '#881337', tab_active_border: '#fb7185', success: '#059669', danger: '#e11d48', warning: '#ea580c' } },
  { name: 'Slate Gray', config: { nav_from: '#334155', nav_to: '#475569', nav_active: '#94a3b8', primary: '#475569', primary_dark: '#334155', accent: '#64748b', header_bg: '#f1f5f9', bg_light: '#f8fafc', tab_active_text: '#334155', tab_active_border: '#64748b', text: '#1e293b', text_secondary: '#64748b', border: '#cbd5e1' } },
  { name: 'Forest Olive', config: { nav_from: '#365314', nav_to: '#3f6212', nav_active: '#84cc16', primary: '#3f6212', primary_dark: '#365314', accent: '#84cc16', header_bg: '#ecfccb', bg_light: '#f7fee7', tab_active_text: '#365314', tab_active_border: '#84cc16', success: '#16a34a', danger: '#dc2626', warning: '#ca8a04' } },
  { name: 'Copper Bronze', config: { nav_from: '#7c2d12', nav_to: '#9a3412', nav_active: '#fb923c', primary: '#9a3412', primary_dark: '#7c2d12', accent: '#f97316', header_bg: '#ffedd5', bg_light: '#fff7ed', tab_active_text: '#7c2d12', tab_active_border: '#fb923c', success: '#15803d', danger: '#b91c1c', warning: '#d97706' } },
  { name: 'Sapphire Blue', config: { nav_from: '#1e3a8a', nav_to: '#1d4ed8', nav_active: '#60a5fa', primary: '#1d4ed8', primary_dark: '#1e3a8a', accent: '#3b82f6', header_bg: '#dbeafe', bg_light: '#eff6ff', tab_active_text: '#1e3a8a', tab_active_border: '#3b82f6', text: '#1e293b', border: '#bfdbfe' } },
  { name: 'Tropical Aqua', config: { nav_from: '#134e4a', nav_to: '#115e59', nav_active: '#2dd4bf', primary: '#115e59', primary_dark: '#134e4a', accent: '#14b8a6', header_bg: '#ccfbf1', bg_light: '#f0fdfa', tab_active_text: '#134e4a', tab_active_border: '#14b8a6', success: '#059669', danger: '#dc2626', warning: '#d97706' } },
  { name: 'Indigo Night', config: { nav_from: '#312e81', nav_to: '#3730a3', nav_active: '#a78bfa', primary: '#3730a3', primary_dark: '#312e81', accent: '#818cf8', header_bg: '#e0e7ff', bg_light: '#eef2ff', tab_active_text: '#312e81', tab_active_border: '#818cf8', success: '#16a34a', danger: '#dc2626', warning: '#d97706' } },
];

function PmsThemeSettings({ token, selectedProperty, onThemeApplied }) {
  const [config, setConfig] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const previewTimer = useRef(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadTheme = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/general/theme', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load theme');
      setConfig(data || {});
      setSaved(data || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, headers]);

  useEffect(() => { loadTheme(); }, [loadTheme]);

  // Live preview: apply changes in real-time as user picks colors
  const updateField = (key, value) => {
    setConfig(prev => {
      const next = { ...prev, [key]: value };
      // Debounce live preview
      clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(() => applyTheme(next), 80);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      // Filter out values that match defaults (store only overrides)
      const payload = {};
      for (const [k, v] of Object.entries(config)) {
        if (v && v !== DEFAULTS[k]) payload[k] = v;
      }
      const res = await fetch('/api/v1/general/theme', {
        method: 'PUT', headers: headers(true), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setSaved(data.theme_config || payload);
      applyTheme(data.theme_config || payload);
      if (onThemeApplied) onThemeApplied(data.theme_config || payload);
      setMessage('Theme saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/general/theme', { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset');
      setConfig({});
      setSaved({});
      resetTheme();
      if (onThemeApplied) onThemeApplied({});
      setMessage('Theme reset to defaults');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setConfig({ ...preset.config });
    applyTheme(preset.config);
  };

  const revertChanges = () => {
    setConfig({ ...saved });
    if (Object.keys(saved).length > 0) {
      applyTheme(saved);
    } else {
      resetTheme();
    }
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(saved);
  const getVal = (key) => config[key] || DEFAULTS[key] || '';

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 900 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Theme Settings</h2>
        <p style={{ color: 'var(--theme-text-muted)' }}>Please select a property to manage theme.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 4, color: 'var(--theme-primary)' }}>Theme Settings</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>
            Customize colors to match your hotel brand. Changes preview in real-time.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {hasChanges && (
            <button onClick={revertChanges} style={{ padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid var(--theme-border)', background: '#fff', color: 'var(--theme-text)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
              Revert
            </button>
          )}
          <button onClick={handleReset} disabled={saving} style={{ padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid var(--theme-danger)', background: '#fff', color: 'var(--theme-danger)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            Reset to Default
          </button>
          <button onClick={handleSave} disabled={saving || !hasChanges} style={{ padding: '0.4rem 0.9rem', borderRadius: 6, border: 'none', background: hasChanges ? 'var(--theme-primary)' : 'var(--theme-border)', color: '#fff', cursor: hasChanges ? 'pointer' : 'default', fontSize: '0.82rem', fontWeight: 600 }}>
            {saving ? 'Saving...' : 'Save Theme'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading theme...</div>
      ) : (
        <>
          {/* Presets */}
          <div style={{ marginBottom: '1.2rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
              Quick Presets
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {PRESETS.map((p) => {
                const bg1 = p.config.nav_from || DEFAULTS.nav_from;
                const bg2 = p.config.nav_to || DEFAULTS.nav_to;
                const acc = p.config.nav_active || DEFAULTS.nav_active;
                return (
                  <button key={p.name} onClick={() => applyPreset(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', borderRadius: 8, border: '1px solid var(--theme-border)', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-text)' }}>
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: bg1, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: bg2, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: acc, border: '1px solid rgba(0,0,0,0.1)' }} />
                    </span>
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color groups */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {GROUPS.map((group) => (
              <div key={group.label} style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 10, padding: '1rem' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {group.label}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {group.fields.map((f) => (
                    <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {f.type === 'color' ? (
                        <>
                          <input
                            type="color"
                            value={getVal(f.key)}
                            onChange={(e) => updateField(f.key, e.target.value)}
                            style={{ width: 36, height: 30, border: '1px solid var(--theme-border)', borderRadius: 4, padding: 1, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-text)' }}>{f.label}</div>
                            <input
                              type="text"
                              value={getVal(f.key)}
                              onChange={(e) => updateField(f.key, e.target.value)}
                              style={{ width: '100%', fontSize: '0.75rem', padding: '0.2rem 0.4rem', border: '1px solid var(--theme-border)', borderRadius: 4, color: 'var(--theme-text-secondary)', fontFamily: 'monospace' }}
                            />
                          </div>
                        </>
                      ) : (
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-text)', marginBottom: 2 }}>{f.label}</div>
                          <input
                            type="text"
                            value={getVal(f.key)}
                            onChange={(e) => updateField(f.key, e.target.value)}
                            style={{ width: '100%', fontSize: '0.8rem', padding: '0.35rem 0.5rem', border: '1px solid var(--theme-border)', borderRadius: 4 }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Live Preview */}
          <div style={{ marginTop: '1.5rem', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--theme-primary)', textTransform: 'uppercase', letterSpacing: '0.03em', padding: '1rem 1rem 0.5rem' }}>
              Live Preview
            </div>
            {/* Nav preview */}
            <div style={{
              display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.6rem 1rem', flexWrap: 'wrap',
              background: `linear-gradient(135deg, ${getVal('nav_from')} 0%, ${getVal('nav_to')} 100%)`,
            }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', marginRight: '0.5rem' }}>
                {selectedProperty?.name || 'Hotel Name'}
              </span>
              {['Hotel Config', 'Front Office', 'Housekeeping'].map((lbl, i) => (
                <button key={lbl} style={{
                  padding: '0.3rem 0.7rem', borderRadius: 5, border: '1px solid rgba(255,255,255,0.2)',
                  background: i === 1 ? getVal('nav_active') : 'rgba(255,255,255,0.09)',
                  color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'default',
                  boxShadow: i === 1 ? `0 2px 8px ${getVal('nav_active')}55` : 'none',
                }}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* Sub-nav preview */}
            <div style={{ display: 'flex', gap: 0, background: '#fff', borderBottom: '2px solid #e8ecf3', padding: '0 0.5rem' }}>
              {['Dashboard', 'Command Center', 'Reservations'].map((lbl, i) => (
                <span key={lbl} style={{
                  padding: '0.5rem 0.8rem', fontSize: '0.82rem', fontWeight: i === 0 ? 700 : 500,
                  color: i === 0 ? getVal('tab_active_text') : '#6b7280',
                  borderBottom: i === 0 ? `2px solid ${getVal('tab_active_border')}` : '2px solid transparent',
                }}>
                  {lbl}
                </span>
              ))}
            </div>
            {/* Content preview */}
            <div style={{ padding: '1rem', background: getVal('bg_light') }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem' }}>
                <button style={{ padding: '0.35rem 0.8rem', borderRadius: 5, border: 'none', background: getVal('primary'), color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'default' }}>Primary Button</button>
                <button style={{ padding: '0.35rem 0.8rem', borderRadius: 5, border: 'none', background: getVal('accent'), color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'default' }}>Accent Button</button>
                <button style={{ padding: '0.35rem 0.8rem', borderRadius: 5, border: 'none', background: getVal('success'), color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'default' }}>Success</button>
                <button style={{ padding: '0.35rem 0.8rem', borderRadius: 5, border: 'none', background: getVal('danger'), color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'default' }}>Danger</button>
                <button style={{ padding: '0.35rem 0.8rem', borderRadius: 5, border: 'none', background: getVal('warning'), color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'default' }}>Warning</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={{ background: getVal('header_bg'), color: getVal('text'), padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${getVal('border')}` }}>Room</th>
                    <th style={{ background: getVal('header_bg'), color: getVal('text'), padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${getVal('border')}` }}>Guest</th>
                    <th style={{ background: getVal('header_bg'), color: getVal('text'), padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${getVal('border')}` }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={{ padding: '0.4rem 0.6rem', color: getVal('text'), borderBottom: `1px solid ${getVal('border_light')}` }}>101</td><td style={{ padding: '0.4rem 0.6rem', color: getVal('text_secondary'), borderBottom: `1px solid ${getVal('border_light')}` }}>John Smith</td><td style={{ padding: '0.4rem 0.6rem', borderBottom: `1px solid ${getVal('border_light')}` }}><span style={{ background: '#dcfce7', color: getVal('success'), padding: '2px 8px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600 }}>Checked In</span></td></tr>
                  <tr><td style={{ padding: '0.4rem 0.6rem', color: getVal('text'), borderBottom: `1px solid ${getVal('border_light')}` }}>205</td><td style={{ padding: '0.4rem 0.6rem', color: getVal('text_secondary'), borderBottom: `1px solid ${getVal('border_light')}` }}>Sarah Johnson</td><td style={{ padding: '0.4rem 0.6rem', borderBottom: `1px solid ${getVal('border_light')}` }}><span style={{ background: '#fef9c3', color: getVal('warning'), padding: '2px 8px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600 }}>Reserved</span></td></tr>
                  <tr><td style={{ padding: '0.4rem 0.6rem', color: getVal('text') }}>310</td><td style={{ padding: '0.4rem 0.6rem', color: getVal('text_muted') }}>Available</td><td style={{ padding: '0.4rem 0.6rem' }}><span style={{ background: getVal('border_light'), color: getVal('text_muted'), padding: '2px 8px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600 }}>Vacant</span></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PmsThemeSettings;
